import { getFenixShippingEstimate } from "@utils/api"
import { find, uniq } from "lodash-es"
import React from "react"

type VariantLike = { sku?: null | string } | null | undefined

const findShippingDataForMethod = (data, method) =>
  find(data, ({ shippingMethodDesc }) => shippingMethodDesc === method)

const formatShippingData = (responseData) => {
  const shippingData = {}

  responseData?.forEach((d) => {
    const variants = d.webId?.split(",")
    variants?.forEach((v) => {
      if (shippingData[v]) {
        shippingData[v].push(d)
      } else {
        shippingData[v] = [d]
      }
    })
  })

  return shippingData
}

export default function useFenixShippingEstimates(
  zipCode: string | undefined,
  variants: undefined | VariantLike[],
  shippingMethods: string[] | undefined
) {
  const [invalidShippingData, setInvalidShippingData] = React.useState(false)
  const [shippingDataLoaded, setShippingDataLoaded] = React.useState(false)
  const [shippingData, setShippingData] = React.useState<Record<string, any>>(
    {}
  )
  const [zipCodeError, setZipCodeError] = React.useState(false)
  const [zipCodeErrorMessage, setZipCodeErrorMessage] = React.useState<
    string | undefined
  >()

  const getShippingDataForSku = React.useCallback(
    (sku) => {
      if (
        !sku ||
        !shippingData ||
        !shippingMethods ||
        shippingMethods.length === 0
      )
        return

      const variantShippingMethods = shippingData[sku]
      const [expeditedShipping, expressShipping, standardShipping] =
        shippingMethods

      return (
        variantShippingMethods &&
        (findShippingDataForMethod(variantShippingMethods, expeditedShipping) ||
          findShippingDataForMethod(variantShippingMethods, expressShipping) ||
          findShippingDataForMethod(variantShippingMethods, standardShipping))
      )
    },
    [shippingData]
  )

  // Build a stable set of SKUs for fetching (order doesn't matter to the API)
  const skus = React.useMemo(
    () => uniq((variants || []).map((v) => v?.sku).filter(Boolean) as string[]),
    [variants]
  )

  // Signature for when we should refetch:
  // zip + (sorted SKUs) + shippingMethods (order-invariant)
  const skuKey = React.useMemo(() => skus.slice().sort().join("|"), [skus])
  const methodKey = React.useMemo(
    () => (shippingMethods ? [...shippingMethods].sort().join("|") : ""),
    [shippingMethods]
  )
  const normalizedZip = (zipCode || "").trim()
  const signature = `${normalizedZip}::${skuKey}::${methodKey}`

  // Track the last completed signature and an in-flight id to ignore stale responses
  const lastSignatureRef = React.useRef<string>("")
  const requestIdRef = React.useRef(0)

  React.useEffect(() => {
    // Preconditions
    if (
      !normalizedZip ||
      skus.length === 0 ||
      !shippingMethods ||
      shippingMethods.length === 0
    ) {
      setShippingData({})
      setShippingDataLoaded(false)
      setInvalidShippingData(false)
      setZipCodeError(false)
      setZipCodeErrorMessage(undefined)
      return
    }

    // Only fetch on change
    if (lastSignatureRef.current === signature) return

    lastSignatureRef.current = signature
    const myReq = ++requestIdRef.current

    setShippingDataLoaded(false)
    setInvalidShippingData(false)
    setZipCodeError(false)
    setZipCodeErrorMessage(undefined)

    getFenixShippingEstimate(
      normalizedZip,
      skus.map((sku) => ({ quantity: 1, sku }))
    )
      .then((data) => {
        // Ignore if a newer request has started
        if (requestIdRef.current !== myReq) return

        const formatted = formatShippingData(data)
        setShippingData(formatted)
        setShippingDataLoaded(true)
      })
      .catch((err) => {
        if (requestIdRef.current !== myReq) return

        const msg = err?.message || String(err || "")
        const isZipErr = /zip|postal|invalid/i.test(msg)
        setZipCodeError(isZipErr)
        setZipCodeErrorMessage(msg)
        setInvalidShippingData(true)
        setShippingData({})
        setShippingDataLoaded(true)
      })
  }, [signature, normalizedZip, skus, shippingMethods])

  return {
    getShippingDataForSku,
    invalidShippingData,
    shippingData,
    shippingDataLoaded,
    zipCodeError,
    zipCodeErrorMessage,
  }
}
